import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Chip, IconButton, Skeleton, SvgIcon, TableSortLabel, Tooltip, Typography, useTheme } from '@mui/material';
import { User } from '../../../../app/services/api/generated';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import useLogError from '../../../../hooks/useLogError';
import CustomPagination from '../../../../components/forms/Pagination';
import { Trash01 } from '../../../../assets';
import CreateUpdateUserModal from './CreateUpdateUserModal';
import { UpsertUserRequest } from '../types/UpsertUserRequest';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { ConfirmationDeleteDialog } from '../../../../components/shared/dialogs/ConfirmationDeleteDialog';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { UserContext } from '../../../../components/shared/useUser';

type AccessLevelType = 'tenant' | 'franchise' | 'store' | 'none';

interface AccessInfo {
    level: AccessLevelType;
    scope: string;
    count: number;
}

const getAccessInfo = (user: User): AccessInfo => {

  if (user.tenants && user.tenants.length > 0) {
    const tenantNames = user.tenants.map(t => t.name).filter(Boolean).join(', ');
    return {
      level: 'tenant',
      scope: tenantNames || 'Unknown tenant',
      count: user.tenants.length,
    };
  }

  if (user.franchises && user.franchises.length > 0) {
    const franchiseNames = user.franchises.map(f => f.franchiseName).filter(Boolean).join(', ');
    return {
      level: 'franchise',
      scope: franchiseNames || 'Unknown franchise',
      count: user.franchises.length,
    };
  }

  if (user.stores && user.stores.length > 0) {
    const storeCount = user.stores.length;
    const storeNames = user.stores.slice(0, 3).map(s => s.storeName || s.storeNumber).join(', ');
    const scope = storeCount > 3 ? `${storeNames} +${storeCount - 3} more` : storeNames;
    return {
      level: 'store',
      scope: scope || 'Unknown stores',
      count: storeCount,
    };
  }

  return { level: 'none', scope: 'No access configured', count: 0 };
};

interface UsersTableProps {
    list: User[];
    loadData: () => void;
    isLoading: boolean;
    searchTerm: string;
    clearAll: () => void;
    page: number;
    maxPages: number;
    setPage: (page: number) => void;
    onCreate: () => void;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSortChange: (sortBy: string, sortDirection: 'asc' | 'desc') => void;
}

const UsersTable = ({ list, loadData, isLoading, searchTerm, clearAll, page, maxPages, setPage, onCreate, sortBy, sortDirection, onSortChange }: UsersTableProps) => {
  const { t } = useTranslation('manageUsers');
  const { logError } = useLogError();
  const theme = useTheme();
  const { deleteUser, editUser } = useNswagClient();
  const [selectedUser, setSelectedUser] = React.useState<User | undefined>(undefined);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showDeleteSuccessMessage, setshowDeleteSuccessMessage] = React.useState(false);
  const [showUpdateSuccessMessage, setShowUpdateSuccessMessage] = React.useState(false);
  const [showResetPasswordSuccessMessage, setShowResetPasswordSuccessMessage] = React.useState(false);
  const { hasPermissionTo } = React.useContext(UserContext);

  const handleSort = (property: string) => {
    const isCurrentColumn = sortBy === property;
    const newDirection = isCurrentColumn && sortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange(property, newDirection);
  };

  const onEdit = (user: User | undefined) => {
    if (user) {
      setSelectedUser(user);
    }
    setShowEditModal(true);
  };

  const handleEdit = (body: UpsertUserRequest) => {
    editUser(selectedUser?.id ?? 0, body)
      .then(() => {
        setShowUpdateSuccessMessage(true);
        setTimeout(() => loadData(), 2000);
      })
      .catch(logError);
  };

  const onDelete = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDelete = () => {
    deleteUser(selectedUser?.id ?? 0)
      .then(() => {
        setshowDeleteSuccessMessage(true);
        loadData();
      })
      .catch(logError);

    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };

  if (isLoading && list.length === 0) {
    return <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
      variant='rectangular'
      height={445.89} />;
  }

  if (!isLoading && list.length === 0) {
    return <EmptyTable
      text={t('users')}
      searchTerm={searchTerm}
      onClear={clearAll}
      onAdd={onCreate}
      addItemText={t('newUser')}
      onAddIsCreating={false} />;
  }

  return (
    <>
      {isLoading ? <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
        variant='rectangular'
        height={445.89} />
        :
        <TableContainer sx={{ borderRadius: '12px' }}
          component='div'>
          <Table size="small">
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell width={'25%'}>
                  <TableSortLabel
                    active={sortBy === 'emailAddress'}
                    direction={sortDirection}
                    onClick={() => handleSort('emailAddress')}
                  >
                    {t('email')}
                  </TableSortLabel>
                </TableCell>
                <TableCell width={'20%'}>
                  <TableSortLabel
                    active={sortBy === 'persona'}
                    direction={sortDirection}
                    onClick={() => handleSort('persona')}
                  >
                    {t('role')}
                  </TableSortLabel>
                </TableCell>
                <TableCell width={'15%'}>
                  {t('accessLevel')}
                </TableCell>
                <TableCell width={'30%'}>
                  {t('accessScope')}
                </TableCell>
                <TableCell width={'10%'} />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => {
                const accessInfo = getAccessInfo(row);
                const getAccessLevelColors = (level: AccessLevelType) => {
                  switch (level) {
                    case 'tenant':
                      return {
                        background: theme.palette.custom.violet[50],
                        border: theme.palette.custom.violet[200],
                        text: theme.palette.custom.violet[700],
                      };
                    case 'franchise':
                      return {
                        background: theme.palette.success[50],
                        border: theme.palette.success[200],
                        text: theme.palette.success[700],
                      };
                    case 'store':
                      return {
                        background: theme.palette.custom.blue[50],
                        border: theme.palette.custom.blue[200],
                        text: theme.palette.custom.blue[700],
                      };
                    case 'none':
                    default:
                      return {
                        background: theme.palette.custom.gray[50],
                        border: theme.palette.custom.gray[200],
                        text: theme.palette.custom.gray[700],
                      };
                  }
                };

                const colors = getAccessLevelColors(accessInfo.level);
                const accessLevelLabel = {
                  tenant: t('tenantLevel'),
                  franchise: t('franchiseLevel'),
                  store: t('storeLevel'),
                  none: t('none'),
                }[accessInfo.level];

                return (
                  <TableRow
                    key={row.id}
                    onClick={hasPermissionTo(['UserManagementUpdate']) ? () => onEdit(row) : undefined}
                    sx={hasPermissionTo(['UserManagementUpdate']) ? { cursor: 'pointer' } : {}}
                  >
                    <TableCell>
                      <Typography variant='textXS'
                        fontWeight={600}
                        color={theme.palette.primary[500]}
                        sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        {row.emailAddress}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='textXS'>
                        {row.persona?.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={accessLevelLabel}
                        size="small"
                        sx={{
                          backgroundColor: colors.background,
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                          height: '24px',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={accessInfo.scope}
                        arrow>
                        <Typography
                          variant='textXS'
                          sx={{
                            maxWidth: 250,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {accessInfo.scope}
                          {accessInfo.level === 'store' && accessInfo.count > 0 && (
                            <Typography component="span"
                              variant='textXS'
                              color="text.secondary">
                              {` (${accessInfo.count} ${accessInfo.count === 1 ? 'store' : 'stores'})`}
                            </Typography>
                          )}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <IconButton aria-label="delete"
                        disabled={!hasPermissionTo(['UserManagementDelete'])}
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(row);
                        }}>
                        <SvgIcon fontSize='small'>
                          <Trash01 color={theme.palette.error[500]} />
                        </SvgIcon>
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Add empty rows to maintain height */}
              {Array.from({ length: Math.max(0, 10 - (list.length ?? 0)) }).map((_, index) => (
                <TableRow key={`empty-row-${index}`}
                  sx={{ height: '40px', backgroundColor: theme.palette.common.white }}>
                  <TableCell colSpan={5}
                    sx={{
                      backgroundColor: theme.palette.common.white,
                      borderColor: index === Math.max(0, 10 - (list.length ?? 0)) - 1 ? theme.palette.custom.gray[100] : theme.palette.common.white,
                    }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination
        page={page}
        setPage={setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
      />
      <CreateUpdateUserModal
        openModal={showEditModal}
        onConfirm={handleEdit}
        editUser={selectedUser}
        onCancel={handleCloseModal}
        onResetPassword={() => setShowResetPasswordSuccessMessage(true)}
      />
      <ConfirmationDeleteDialog
        open={showDeleteModal}
        onConfirm={handleDelete}
        onCancel={handleCloseModal}
        nameOfObject={t('user')}
      />
      <MessagesSnackbar
        open={showDeleteSuccessMessage}
        onClose={() => setshowDeleteSuccessMessage(false)}
        message={t('userDeleted')}
        severity="success"
        duration={null}
      />
      <MessagesSnackbar
        open={showUpdateSuccessMessage}
        onClose={() => setShowUpdateSuccessMessage(false)}
        message={t('successUpdateMessage')}
        severity="success"
        duration={null}
      />
      <MessagesSnackbar
        open={showResetPasswordSuccessMessage}
        onClose={() => setShowResetPasswordSuccessMessage(false)}
        message={t('resetEmailSent')}
        severity="success"
        duration={null}
      />
    </>
  );
};

export default UsersTable;
